import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Key, Plus, Copy, BookOpen, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

export default function PublisherApiKeys() {
  const [keys, setKeys] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [newKeyShown, setNewKeyShown] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/publisher/api-keys");
      setKeys(data);
    } catch (e) {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  const loadRejected = async () => {
    setRejectedLoading(true);
    try {
      const { data } = await api.get("/publisher/rejected-leads");
      setRejected(data || []);
    } catch (e) { /* non-fatal */ }
    finally { setRejectedLoading(false); }
  };

  useEffect(() => { refresh(); loadRejected(); }, []);

  const createKey = async () => {
    try {
      const { data } = await api.post("/publisher/api-keys", { label: "secondary" });
      setNewKeyShown(data.key);
      await refresh();
    } catch (e) {
      toast.error("Could not create API key");
    }
  };

  const ingestUrl = `${BACKEND_URL}/api/v1/leads/ingest`;

  return (
    <div className="space-y-6" data-testid="publisher-apikeys-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">API & Keys</h1>
        <p className="text-[13px] text-slate-500 mt-1">Push leads from your funnel directly to Connexis routing in real-time.</p>
      </div>

      {/* Quickstart */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
        <div className="flex items-center gap-2">
          <span className="inline-grid place-items-center size-8 rounded-md bg-[#0F172A] text-white"><BookOpen className="size-4" strokeWidth={1.7} /></span>
          <h2 className="text-lg font-semibold text-slate-900">Quickstart · push your first lead</h2>
        </div>
        <p className="mt-2 text-sm text-slate-600">POST a JSON payload to our ingest endpoint with your API key in the <code className="bg-slate-100 rounded px-1 py-0.5">X-API-Key</code> header.</p>
        <pre className="mt-4 rounded-lg bg-[#0F172A] text-white p-4 text-[12px] overflow-x-auto font-mono leading-relaxed">{`curl -X POST ${ingestUrl} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: <YOUR_KEY>" \\
  -d '{
    "sector": "mortgages",
    "first_name": "James",
    "last_name": "Patel",
    "email": "james@example.com",
    "phone": "+447700900000",
    "city": "London",
    "consent_token": "tf_abc123",
    "consent_at": "2026-02-16T10:30:00Z"
  }'`}</pre>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyToClipboard(ingestUrl).then(() => toast.success("Endpoint copied"))} data-testid="pub-copy-endpoint">
            <Copy className="size-3.5" /> Copy endpoint
          </Button>
          <span className="text-[11px] text-slate-500">Returns <code className="bg-slate-100 rounded px-1 py-0.5">{`{accepted, lead_id, price_gbp, publisher_earning_gbp}`}</code></span>
        </div>
      </div>

      {/* Keys */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Key className="size-4 text-[#0055FF]" /> Your API keys</h2>
          <Button size="sm" onClick={createKey} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="pub-create-key"><Plus className="size-3.5" /> Create key</Button>
        </div>
        <div className="mt-4 space-y-2">
          {loading ? (
            <p className="text-[12.5px] text-slate-400"><Loader2 className="size-3.5 animate-spin inline mr-1" /> Loading…</p>
          ) : keys.length === 0 ? (
            <p className="text-[12.5px] text-slate-400 italic">No keys yet — create one to start pushing leads.</p>
          ) : keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-2.5 text-sm" data-testid={`pub-key-${k.id}`}>
              <div>
                <div className="font-mono text-slate-900 text-[12.5px]">{k.key_preview}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{k.label} · created {new Date(k.created_at).toLocaleDateString("en-GB")}{k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString("en-GB")}` : ""}</div>
              </div>
              <span className={`text-[11px] uppercase font-medium tracking-widest ${k.active ? "text-emerald-600" : "text-slate-400"}`}>{k.active ? "Active" : "Revoked"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Rejected */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6" data-testid="pub-rejected-panel">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Inbox className="size-4 text-rose-500" /> Recent rejections</h2>
          <Button size="sm" variant="outline" className="rounded-full h-7 text-[11.5px]" onClick={loadRejected} data-testid="pub-load-rejected">{rejectedLoading ? <Loader2 className="size-3 animate-spin" /> : "Refresh"}</Button>
        </div>
        <p className="text-[12.5px] text-slate-500 mt-1">Why ingests were rejected — useful for debugging your integration.</p>
        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
          {rejected.length === 0 ? (
            <p className="text-[12.5px] text-slate-400 italic">No rejections recently — clean integration!</p>
          ) : rejected.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-[12.5px]">
              <div className="min-w-0">
                <div className="text-slate-900 font-medium">{r.reason?.replace(/_/g, " ")}</div>
                <div className="text-[11px] text-slate-500">{r.email || "—"} · {r.sector} · {new Date(r.created_at).toLocaleString("en-GB")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!newKeyShown} onOpenChange={(o) => !o && setNewKeyShown(null)}>
        <DialogContent className="max-w-md" data-testid="new-key-dialog">
          <DialogHeader>
            <DialogTitle>Your new API key</DialogTitle>
            <DialogDescription>This key is shown once. Save it somewhere safe.</DialogDescription>
          </DialogHeader>
          <pre className="rounded-lg bg-[#0F172A] text-[#3D7BFF] p-4 text-[12px] font-mono break-all whitespace-pre-wrap" data-testid="new-key-value">{newKeyShown}</pre>
          <DialogFooter>
            <Button onClick={() => copyToClipboard(newKeyShown).then(() => toast.success("Key copied"))} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]"><Copy className="size-3.5" /> Copy</Button>
            <Button variant="outline" onClick={() => setNewKeyShown(null)} className="rounded-full">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
